import { PROJECTOR, SUPPORTED_PROJECTOR_MESH_FORMATS_LABEL } from '@/constants/object';
import { useAssetStore } from '@/stores/api/asset';
import { useProjectorStore } from '@/stores/api/projector';
import { useTimelinePlayerStore } from '@/stores/ui/timelinePlayer';
import { resolveInputSource } from '@/threejs/canvas-patterns';
import { render } from '@/threejs/core/scene';
import eventBus from '@/utils/eventBus';
import { buildLookAtQuaternion } from '@/utils/projectorCalculations';
import JSZip from 'jszip';
import * as THREE from 'three';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { addProjector, removeProjector } from '../core';
import { updateProjectorHelperLines } from '../core/projectorHelperLines';
import { ensureObjectAboveGrid, getObjectByCode } from '../objectManager';
import { textureManager } from '../textureManager';
import { BaseObject } from './base';

export class Projector extends BaseObject {
  constructor(options = {}) {
    super();
    const data = options.data;
    this.isMeshUrl = options.data.mesh != null;
    this.userData.code = options.code;
    this.userData.isProjector = true;

    this.userData.throwRatio = data.throwRatio ?? 1.6;
    this.userData.lumens = data.lumens ?? 5500;
    this.userData.aspect = data.aspect ?? 16 / 9;
    this.userData.resolution =
      data.resolution ?? new THREE.Vector2(data.resolutionWidth ?? 1920, data.resolutionHeight ?? 1080);
    this.userData.resolutionWidth = this.userData.resolution.x;
    this.userData.resolutionHeight = this.userData.resolution.y;
    this.userData.power_brightness = data.power_brightness ?? 100;
    this.userData.target = new THREE.Vector3(data.target?.x ?? 0, data.target?.y ?? 0, data.target?.z ?? 0);
    this.userData.gain = data.gain ?? 0.5;
    this.userData.roll = (data.rotation?.z ?? 0) * (Math.PI / 180);

    this.userData.stackOffset = new THREE.Vector3(
      data.stackOffset?.x ?? 0,
      data.stackOffset?.y ?? 0.35,
      data.stackOffset?.z ?? 0
    );

    this._applySeq = 0;

    this.bodyMaterial = new THREE.MeshStandardMaterial({
      color: 0x4a4a4a,
      metalness: 0.2,
      roughness: 0.7,
    });
    this.lensMaterial = new THREE.MeshStandardMaterial({
      color: 0x0b0d12,
      metalness: 0.9,
      roughness: 0.2,
      emissive: 0x1f3d5a,
      emissiveIntensity: 0.4,
      opacity: 0,
    });

    this.stackedMeshGroup = new THREE.Group();
    this.stackedMeshGroup.name = 'stackedMeshes';
    this.add(this.stackedMeshGroup);

    this.applyProperties(data);

    this.camera = new THREE.PerspectiveCamera(40, this.userData.aspect, 0.2, 55);
    this.add(this.camera);

    if (data.mesh) {
      this._loadMesh(data.mesh);
    } else {
      this._buildFallbackMesh();
      this.getOutline();
    }

    this.updateTarget(this.userData.target);
    this.updateTexture();
    this.updateCamera();
    this.updateStackedMeshes();

    if (this.userData.power !== false) {
      addProjector(this);
    }

    // Apply gain after lens is created (default: 0.5)
    this.applyGain(this.userData.gain);
  }

  async _loadMesh(url) {
    this.userData.mesh = url;
    try {
      // Remove old body and lens if they exist
      if (this.body) {
        this.remove(this.body);
        this.body.geometry?.dispose();
      }
      if (this.lens) {
        this.remove(this.lens);
        this.lens.geometry?.dispose();
      }

      const buffer = await fetch(url).then((r) => r.arrayBuffer());
      const objText = await this._decodeBuffer(buffer);
      const loaded = objText.trimStart().startsWith('{')
        ? new THREE.ObjectLoader().parse(JSON.parse(objText))
        : new OBJLoader().parse(objText);

      const meshes = [];
      loaded.traverse((child) => {
        if (child.isMesh) meshes.push(child);
      });

      const bodyMesh = meshes.find((m) => m.name.toLowerCase().includes('body')) ?? meshes[0] ?? null;
      const lensMesh = meshes.find((m) => m.name.toLowerCase().includes('lens')) ?? meshes[1] ?? null;
      if (bodyMesh) {
        const geo = bodyMesh.geometry.clone();
        geo.computeBoundingBox();
        const center = new THREE.Vector3();
        geo.boundingBox.getCenter(center);
        geo.translate(-center.x, -center.y, -center.z);

        const size = new THREE.Vector3();
        geo.boundingBox.getSize(size);
        const scale = Math.min(1 / size.x, 1 / size.y, 1 / size.z);

        this.body = new THREE.Mesh(geo, this.bodyMaterial);
        this.body.position.set(0, 0, 0.33);
        this.body.scale.setScalar(scale);
        this.body.rotation.x = -(Math.PI / 2);
        this._markProjectorMesh(this.body);
        this.body.updateMatrixWorld(true);

        const positions = geo.attributes.position;
        const wp = new THREE.Vector3();
        const box = new THREE.Box3().setFromObject(this.body);

        const axes = [
          { axis: 'x', sign: 1, tip: box.max.x, along: ['y', 'z'], dir: new THREE.Vector3(1, 0, 0) },
          { axis: 'x', sign: -1, tip: box.min.x, along: ['y', 'z'], dir: new THREE.Vector3(-1, 0, 0) },
          { axis: 'y', sign: 1, tip: box.max.y, along: ['x', 'z'], dir: new THREE.Vector3(0, 1, 0) },
          { axis: 'y', sign: -1, tip: box.min.y, along: ['x', 'z'], dir: new THREE.Vector3(0, -1, 0) },
          { axis: 'z', sign: 1, tip: box.max.z, along: ['x', 'y'], dir: new THREE.Vector3(0, 0, 1) },
          { axis: 'z', sign: -1, tip: box.min.z, along: ['x', 'y'], dir: new THREE.Vector3(0, 0, -1) },
        ];

        let bestCandidate = null;
        let bestSpread = Infinity;

        for (const { axis, sign, tip, along, dir } of axes) {
          const depth = (box.max[axis] - box.min[axis]) * 0.05;
          let s0 = 0,
            s1 = 0,
            spread = 0,
            cnt = 0;

          for (let i = 0; i < positions.count; i++) {
            wp.fromBufferAttribute(positions, i).applyMatrix4(this.body.matrixWorld);
            const near = sign > 0 ? wp[axis] >= tip - depth : wp[axis] <= tip + depth;
            if (!near) continue;
            s0 += wp[along[0]];
            s1 += wp[along[1]];
            cnt++;
          }

          if (!cnt) continue;
          const c0 = s0 / cnt,
            c1 = s1 / cnt;
          for (let i = 0; i < positions.count; i++) {
            wp.fromBufferAttribute(positions, i).applyMatrix4(this.body.matrixWorld);
            const near = sign > 0 ? wp[axis] >= tip - depth : wp[axis] <= tip + depth;
            if (!near) continue;
            const da = wp[along[0]] - c0,
              db = wp[along[1]] - c1;
            spread += da * da + db * db;
          }
          spread /= cnt;

          if (spread < bestSpread) {
            bestSpread = spread;
            bestCandidate = {
              position: new THREE.Vector3(
                along.includes('x') ? c0 : axis === 'x' ? tip : 0,
                along.includes('y') ? (along[0] === 'y' ? c0 : c1) : axis === 'y' ? tip : 0,
                along.includes('z') ? (along[1] === 'z' ? c1 : c0) : axis === 'z' ? tip : 0
              ),
              axis,
              sign,
              dir,
            };
          }
        }

        if (bestCandidate) {
          const yawDeg = THREE.MathUtils.radToDeg(Math.atan2(bestCandidate.dir.x, bestCandidate.dir.z));
          const lensLocalPos = this.body.worldToLocal(bestCandidate.position.clone());
          if (yawDeg !== 180) {
            this.body.rotation.x = Math.PI / 2;
            this.body.updateMatrixWorld(true);
          }
          this.lensPosition = lensLocalPos.applyMatrix4(this.body.matrixWorld);
          this.lensDirection = bestCandidate.dir;
        } else {
          this.lensPosition = new THREE.Vector3(0, 0, box.max.z);
          this.lensDirection = new THREE.Vector3(0, 0, 1);
        }
      }

      // Offset body so the lens sits at local origin (0,0,0)
      // This makes this.position = lens world position
      if (this.lensPosition) {
        this.body.position.sub(this.lensPosition);
      }

      this.lens = lensMesh ? new THREE.Mesh(lensMesh.geometry.clone(), this.lensMaterial) : this._makeLensMesh();

      this.updateMatrixWorld(true);

      // Lens and camera at local origin since origin = lens
      this.lens.position.set(0, 0, 0);
      this._markProjectorMesh(this.lens, true);
      this.add(this.body, this.lens);

      this.camera.position.set(0, 0, 0);

      // Re-orient to target — rotateToTarget now uses lens position so frustum centers on target
      this.rotateToTarget(this.userData.target);
      this.applyGain(this.userData.gain);
      this.updateStackedMeshes();
      this.getOutline();

      // Re-align newly added projector so its bottom sits on the grid after custom mesh load
      if (this.userData._isNew) {
        ensureObjectAboveGrid(this);
        delete this.userData._isNew;
        if (this.userData.data) delete this.userData.data._isNew;
      }

      if (typeof render === 'function') render();
    } catch (err) {
      console.error('Error loading projector mesh:', err);
      eventBus.emit('modelLoadError', {
        type: 'projector_mesh',
        message: err.message || 'Unknown error loading projector mesh',
        formats: SUPPORTED_PROJECTOR_MESH_FORMATS_LABEL,
      });
      this._buildFallbackMesh();
      if (typeof render === 'function') render();
    }
  }

  async _decodeBuffer(buffer) {
    const header = new Uint8Array(buffer, 0, 2);
    if (header[0] === 0x50 && header[1] === 0x4b) {
      const zip = await JSZip.loadAsync(buffer);
      const file = Object.values(zip.files).find((f) => !f.dir);
      if (!file) throw new Error('No file found inside ZIP');
      return file.async('string');
    }
    return new TextDecoder().decode(buffer);
  }

  _buildFallbackMesh() {
    this.body = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.25, 0.8), this.bodyMaterial);
    this.body.position.z = 0.49;
    this._markProjectorMesh(this.body);

    this.lens = this._makeLensMesh();

    this.lens.position.z = 0;
    this._markProjectorMesh(this.lens, true);
    this.add(this.body, this.lens);
    this.camera.position.copy(this.lens.position);
  }

  _makeLensMesh() {
    const lens = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.18, 24), this.lensMaterial);
    lens.rotation.x = Math.PI / 2;
    return lens;
  }

  _markProjectorMesh(mesh, isLens = false) {
    mesh.userData.projectorOverlayIgnore = true;
    mesh.layers.set(0);
    if (isLens) mesh.layers.enable(0);
  }

  setSelected(selected) {
    super.setSelected(selected);
    // Hide/show target based on selection (only if power is on)
    if (this.userData.power !== false) {
      const target = getObjectByCode(this.userData.code + '-target');
      if (target) target.visible = selected && this.visible;
    }
    updateProjectorHelperLines();
  }

  updateStackedMeshes() {
    if (!this.stackedMeshGroup || !this.bodyMaterial || !this.lensMaterial) return;

    while (this.stackedMeshGroup.children.length > 0) {
      const child = this.stackedMeshGroup.children[0];
      this.stackedMeshGroup.remove(child);
      child.geometry?.dispose();
    }

    const stackCount = this.userData.stackedProjectors - 1 || 0;
    if (stackCount <= 0 || !this.body || !this.lens) return;

    const offset = this.userData.stackOffset ?? new THREE.Vector3(0, 0.35, 0);

    for (let i = 1; i <= stackCount; i++) {
      // Clone body — preserves geometry, rotation, scale, material
      const bodyCopy = this.body.clone();
      bodyCopy.position.set(
        this.body.position.x + i * offset.x,
        this.body.position.y + i * offset.y,
        this.body.position.z + i * offset.z
      );
      bodyCopy.userData.projectorOverlayIgnore = true;
      bodyCopy.layers.set(1);

      // Clone lens — preserves geometry, rotation, position relative to body
      const lensCopy = this.lens.clone();
      lensCopy.position.set(
        this.lens.position.x + i * offset.x,
        this.lens.position.y + i * offset.y,
        this.lens.position.z + i * offset.z
      );
      lensCopy.userData.projectorOverlayIgnore = true;
      lensCopy.layers.set(1);
      lensCopy.layers.enable(0);

      this.stackedMeshGroup.add(bodyCopy, lensCopy);
    }
  }

  dispose() {
    if (this.userData._videoRaf) {
      cancelAnimationFrame(this.userData._videoRaf);
      this.userData._videoRaf = null;
    }
    if (this.userData._videoEl) {
      this.userData._videoEl.pause();
      this.userData._videoEl = null;
      this.userData._videoSrc = null;
    }
    this.userData.inputSource = null;
    this.userData._imgSrc = null;
    this.userData.sourceUrl = null;
    this.userData.sourceAssetType = null;
    this.userData.source = null;
    if (this.userData.texture) {
      this.userData.texture.dispose();
      this.userData.texture = null;
    }
  }

  updateTexture() {
    // Cancel any running video RAF loop when switching patterns
    if (this.userData._videoRaf) {
      cancelAnimationFrame(this.userData._videoRaf);
      this.userData._videoRaf = null;
    }
    if (this.userData._videoEl) {
      this.userData._videoEl.pause();
      this.userData._videoEl = null;
      this.userData._videoSrc = null;
    }
    this.userData._imgSrc = null;

    if (this.userData.power === false) {
      if (this.userData.texture) {
        this.userData.texture.dispose();
        this.userData.texture = null;
      }
      return;
    }

    if (!this.userData.texture) {
      const canvas = Object.assign(document.createElement('canvas'), {
        width: this.userData.resolutionWidth,
        height: this.userData.resolutionHeight,
      });
      this.userData.texture = Object.assign(new THREE.CanvasTexture(canvas), {
        colorSpace: THREE.SRGBColorSpace,
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        wrapS: THREE.ClampToEdgeWrapping,
        wrapT: THREE.ClampToEdgeWrapping,
      });
    }

    const { image: canvas } = this.userData.texture;
    const ctx = canvas.getContext('2d');
    if (ctx) resolveInputSource(this.userData.inputSource).draw(ctx, canvas.width, canvas.height, this.userData);
    this.userData.texture.needsUpdate = true;
    render();
  }

  updateCamera() {
    if (!this.camera) return;
    this.camera.fov = THREE.MathUtils.radToDeg(
      2 * Math.atan(1 / (2 * this.userData.throwRatio * this.userData.aspect))
    );
    this.camera.aspect = this.userData.aspect;
    this.camera.near = 0.2;
    this.camera.far = 1000;
    this.camera.updateProjectionMatrix();
  }

  updatePosition(pos) {
    this.position.copy(pos);
  }

  updateRotation(rot) {
    this.rotation.copy(rot);
  }

  getLensWorldPosition() {
    // Origin is now at the lens, so world position = lens position
    return this.getWorldPosition(new THREE.Vector3());
  }

  rotateToTarget(targetWorldPosition) {
    // Use lens world position so frustum centers on the target
    const lensWorld = this.getLensWorldPosition();
    const dir = new THREE.Vector3().subVectors(targetWorldPosition, lensWorld);
    if (dir.lengthSq() === 0) return;
    // Convert direction from world space to parent's local space
    // (this.quaternion is local to parent, so the direction must be too)
    if (this.parent && this.parent.isScene !== true) {
      const parentWorldQuat = new THREE.Quaternion();
      this.parent.getWorldQuaternion(parentWorldQuat);
      dir.applyQuaternion(parentWorldQuat.invert());
    }

    dir.normalize();

    const prevRight = new THREE.Vector3(1, 0, 0).applyQuaternion(this.quaternion);
    const { quaternion } = buildLookAtQuaternion(dir, {
      roll: this.userData.roll || 0,
      degenerateRight: prevRight,
    });
    this.quaternion.copy(quaternion);

    this.updateMatrixWorld();
  }

  applyGain(gain) {
    this.userData.gain = gain ?? 0.5;

    // Apply gain to lens emissive intensity for visual feedback
    if (this.lens && this.lens.material) {
      if (this.userData.gain > 0) {
        // Set emissive color to blue for projector lens
        this.lens.material.emissive = new THREE.Color(0x1f3d5a);
        this.lens.material.emissiveIntensity = this.userData.gain;
      } else {
        // Turn off emissive when gain is 0
        this.lens.material.emissive = new THREE.Color(0x000000);
        this.lens.material.emissiveIntensity = 0;
      }
      this.lens.material.needsUpdate = true;
    }
  }

  updateTarget(target) {
    const lensWorld = this.getLensWorldPosition();
    const dir = new THREE.Vector3().subVectors(target, lensWorld);
    const dist = dir.length();
    if (dist < 0.01) {
      dir.set(0, 0, -1).multiplyScalar(0.01);
      target = lensWorld.clone().add(dir);
    }
    this.userData.target.copy(target);
    this.rotateToTarget(this.userData.target);
    const mesh = getObjectByCode(this.userData.code + '-target');
    if (mesh) mesh.position.copy(this.userData.target);
  }

  async applyProperties(props) {
    if (this.isTransforming) return;

    let positionDelta = null;

    if ('position' in props) {
      const oldPos = this.position.clone();
      this.updatePosition(new THREE.Vector3(props.position.x, props.position.y, props.position.z));
      const delta = new THREE.Vector3().subVectors(this.position, oldPos);
      if (delta.lengthSq() > 0.000001) {
        positionDelta = delta;
      }
    }

    // If position moved, update target first (if it also changed), then re-orient
    if (positionDelta) {
      // Check if target also changed (e.g. lockTarget off — target moves with position)
      if ('target' in props) {
        const propsTarget = new THREE.Vector3(props.target.x, props.target.y, props.target.z);
        if (!propsTarget.equals(this.userData.target)) {
          this.userData.target.copy(propsTarget);
          const mesh = getObjectByCode(this.userData.code + '-target');
          if (mesh) mesh.position.copy(propsTarget);
        }
      }
      this.rotateToTarget(this.userData.target);
    }

    if ('rotation' in props) {
      const { x = 0, y = 0, z = 0 } = props.rotation;
      this.userData.roll = z * (Math.PI / 180);

      if (!('target' in props)) {
        // Panel path (no target): set Euler directly
        this.rotation.set(x * (Math.PI / 180), y * (Math.PI / 180), z * (Math.PI / 180));
        this.updateMatrixWorld();
      }
      // Gizmo path (target present): skip rotation set — updateTarget below will call rotateToTarget
    }

    if ('lumens' in props) this.userData.lumens = Number(props.lumens) || this.userData.lumens;
    if ('throwRatio' in props) {
      this.userData.throwRatio = Math.min(
        Math.max(Number(props.throwRatio) || this.userData.throwRatio, PROJECTOR.MIN_THROW_RATIO),
        PROJECTOR.MAX_THROW_RATIO
      );
      this.updateCamera();
    }
    if ('visible' in props && this.visible !== props.visible) {
      this.visible = props.visible;
      // Also hide/show the target mesh
      const target = getObjectByCode(this.userData.code + '-target');
      if (target) target.visible = props.visible && (this.userData.selected || false);
    }

    if ('lock' in props) this.userData.lock = props.lock;
    if ('lockTarget' in props) this.userData.lockTarget = props.lockTarget;
    if ('solidColor' in props) {
      const changed = this.userData.solidColor !== props.solidColor;
      this.userData.solidColor = props.solidColor;
      if (changed && this.userData.inputSource === 'solid') {
        this.updateTexture();
      }
    }

    if ('power_brightness' in props) this.userData.power_brightness = props.power_brightness;

    const targetChanged =
      'target' in props &&
      !new THREE.Vector3(props.target.x, props.target.y, props.target.z).equals(this.userData.target);

    if (targetChanged) {
      this.updateTarget(new THREE.Vector3(props.target.x, props.target.y, props.target.z));
    }

    if ('power' in props && this.userData.power !== props.power) {
      this.userData.power = props.power;
      this.updateTexture();
      props.power ? addProjector(this) : removeProjector(this);
      // Show/hide target based on power and selection state
      const target = getObjectByCode(this.userData.code + '-target');
      if (target) target.visible = props.power && this.visible && (this.userData.selected || false);
    }
    if (!this.userData.render) {
      this.userData.render = render;
      this.userData.timelinePlayer = useTimelinePlayerStore();
      this.userData.textureManager = textureManager;
    }

    // Sync media timing data from module to userData
    if ('mediaStartTime' in props) this.userData.mediaStartTime = props.mediaStartTime;
    if ('mediaDuration' in props) this.userData.mediaDuration = props.mediaDuration;
    if ('mediaStart' in props) this.userData.mediaStart = props.mediaStart;

    // Handle source asset change
    if ('source' in props && props.source && this.userData.source !== props.source) {
      this.userData.source = props.source;
      this.userData.sourceUrl = null;
      this.userData.sourceAssetType = null;
      this.userData._imgSrc = null;
      this.userData._videoSrc = null;
      this.userData.sourceLoading = true;
      this.updateTexture();

      const seq = ++this._applySeq;
      try {
        const assetStore = useAssetStore();
        const asset = await assetStore.fetch(props.source);
        if (seq !== this._applySeq) return;
        this.userData.sourceLoading = false;
        this.userData.sourceUrl = asset?.latest_version?.playback_url || asset?.latest_version?.file;
        if (this.userData.sourceUrl) {
          this.userData.sourceAssetType = asset.asset_type;
          this.updateTexture();
        }
      } catch (e) {
        this.userData.sourceLoading = false;
        console.warn('Failed to fetch projector source asset:', props.source, e);
      }
    }

    // Handle pattern change
    if ('inputSource' in props && this.userData.inputSource !== props.inputSource) {
      this.userData.inputSource = props.inputSource;
      if (props.inputSource !== 'source') {
        this.userData.sourceUrl = null;
        this.userData.sourceAssetType = null;
        this.userData.source = null;
        this.userData._imgSrc = null;
        this.userData._videoSrc = null;
      }
      this.updateTexture();
    }
    if ('gain' in props) this.applyGain(props.gain);

    if ('lensShiftHorizontal' in props || 'lensShiftVertical' in props) {
      const lensShiftHorizontal = props.lensShiftHorizontal ?? this.userData.lensShiftHorizontal ?? 0;
      const lensShiftVertical = props.lensShiftVertical ?? this.userData.lensShiftVertical ?? 0;
      this.userData.lensShiftHorizontal = lensShiftHorizontal;
      this.userData.lensShiftVertical = lensShiftVertical;

      if (this.camera) {
        const { x: rw, y: rh } = this.userData.resolution;
        this.camera.setViewOffset(
          rw,
          rh,
          ((lensShiftHorizontal / 100) * rw) / 2,
          ((lensShiftVertical / 100) * -rh) / 2,
          rw,
          rh
        );
        this.camera.updateProjectionMatrix();
      }
    }

    if ('stackedProjectors' in props) {
      this.userData.stackedProjectors = props.stackedProjectors;
      this.updateStackedMeshes();
    }

    if ('stackOffset' in props) {
      this.userData.stackOffset = new THREE.Vector3(
        props.stackOffset.x ?? this.userData.stackOffset.x ?? 0,
        props.stackOffset.y ?? this.userData.stackOffset.y ?? 0.35,
        props.stackOffset.z ?? this.userData.stackOffset.z ?? 0
      );
      this.updateStackedMeshes();
    }

    if ('frustumEdgeColor' in props && this.userData.frustumEdgeColor !== props.frustumEdgeColor) {
      this.userData.frustumEdgeColor = props.frustumEdgeColor;
      this.userData.edgeColor = new THREE.Color(props.frustumEdgeColor);
    }

    if ('libraryCode' in props) {
      if (this.userData.libraryCode !== props.libraryCode) {
        this.userData.libraryCode = props.libraryCode;
        const projectorStore = useProjectorStore();
        projectorStore.fetch(props.libraryCode).then((projector) => {
          if (projector) this.switchProjector(projector);
        });
      }
    }
  }

  async switchProjector(projector) {
    // Swap mesh if different
    if (projector.mesh && projector.mesh !== this.userData.mesh) {
      this._loadMesh(projector.mesh);
    } else if (!projector.mesh && this.userData.mesh) {
      // New library has no mesh — revert to fallback box geometry
      if (this.body) {
        this.remove(this.body);
        this.body.geometry?.dispose();
      }
      if (this.lens) {
        this.remove(this.lens);
        this.lens.geometry?.dispose();
      }
      this.userData.mesh = null;
      this._buildFallbackMesh();
    }

    // Update resolution
    if (projector.resolution_width && projector.resolution_height) {
      const w = Number(projector.resolution_width);
      const h = Number(projector.resolution_height);
      this.userData.resolution = new THREE.Vector2(w, h);
      this.userData.aspect = w / h;
    }

    // Update throw ratio
    if (projector.throw_ratio) {
      this.userData.throwRatio = Number(projector.throw_ratio);
    }

    // Update lumens
    if (projector.lumens) {
      this.userData.lumens = Number(projector.lumens);
    }

    // Update name and brand
    if (projector.name) {
      this.userData.projectorName = projector.name;
    }
    if (projector.brand) {
      this.userData.brand = projector.brand;
    }
    if (projector.thumbnail) {
      this.userData.thumbnail = projector.thumbnail;
    }

    this.updateCamera();
    render();
  }
}
